import { ClerkLoaded, useAuth } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import AcceptOrgInvitation from '@/pages/auth/AcceptOrgInvitation';
import { ConfirmEmail } from '@/pages/auth/ConfirmEmail';
import { ForgetPassword } from '@/pages/auth/ForgetPassword';
import { JoinOrg } from '@/pages/auth/JoinOrg';
import { ResetPassword } from '@/pages/auth/ResetPassword';
import { SignIn } from '@/pages/auth/SignIn';
import { SignUp } from '@/pages/auth/SignUp';
import { SSOCallback } from '@/pages/auth/SSOCallback';
import { VerifyToken } from '@/pages/auth/VerifyToken';

const isInternalSubDomain = (w: Window & typeof globalThis) => {
  const hostname = w?.location?.hostname;
  const subDomain = hostname?.split('.')?.[0];
  if (!subDomain) return true;
  const internalSubDomains = [
    'localhost',
    'zees-dev',
    'zees',
    'consultants-dev',
    'consultants',
  ];
  return internalSubDomains.includes(subDomain);
};

export const AuthRoutes = () => {
  // custom hooks
  const location = useLocation();
  const { orgId } = useParams();
  const { isLoaded, isSignedIn } = useAuth();
  const isInternal = isInternalSubDomain(window);

  // effects
  useEffect(() => {
    if (orgId) {
      window.localStorage.setItem('active-org-id', orgId);
    }
  }, [orgId]);

  if (!isLoaded) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  // Use orgId from URL params for path checks to support multi-org invitations
  // This ensures that when a user clicks an invitation link for a new org,
  // the path check uses the correct orgId from the URL, not the stale localStorage value
  const currentOrgId = orgId || window.localStorage.getItem('active-org-id');
  const isConfirmPath = location.pathname === `/${currentOrgId}/auth/confirm`;
  const isJoinOrgPath = location.pathname === `/${currentOrgId}/auth/join-org`;
  const isInvitationPath =
    location.pathname === `/${currentOrgId}/auth/accept-org-invitation`;
  const isSSOCallbackPath =
    location.pathname === `/${currentOrgId}/auth/sso-callback`;
  const isVerifyTokenPath =
    location.pathname === `/${currentOrgId}/auth/verify-token`;

  if (
    isSignedIn &&
    !isConfirmPath &&
    !isJoinOrgPath &&
    !isInvitationPath &&
    !isSSOCallbackPath &&
    !isVerifyTokenPath
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthLayout>
      <Routes>
        <Route
          path="/sign-in"
          element={<SignIn isInternalHostname={isInternal} />}
        />
        <Route
          path="/sign-up"
          element={<SignUp isInternalHostname={isInternal} />}
        />
        <Route
          path="/confirm"
          element={
            <ClerkLoaded>
              <ConfirmEmail />
            </ClerkLoaded>
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPassword isInternalHostname={isInternal} />}
        />
        <Route
          path="/forget-password"
          element={<ForgetPassword isInternalHostname={isInternal} />}
        />
        {/* callback routes */}
        <Route path="/join-org" element={<JoinOrg />} />
        <Route path="/verify-token" element={<VerifyToken />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route
          path="/accept-org-invitation"
          element={<AcceptOrgInvitation isInternalHostname={isInternal} />}
        />
      </Routes>
    </AuthLayout>
  );
};
